import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Generalisation of the useUrlTimeRange pattern: one piece of page state
 * mirrored into a URL search param, written with replace:true so intra-page
 * tweaks never pollute browser history (NAV-09), and readable on mount so
 * deep links restore (DATA-09).
 *
 * Rules learned from the Historical deep-link race (LINK-03 / PERS-03):
 *  - Never validate a raw URL value against an async catalog before the
 *    catalog resolves, and never rewrite the URL until it has — pass
 *    `syncWhen: false` while the catalog is loading and re-validate via
 *    the functional setter once it lands.
 *  - All URL writes build on the LIVE URL (window.location.search) and
 *    skip no-op writes, so two writers on the same page (e.g. time range
 *    + series keys) can't clobber each other. (The functional setParams
 *    form does NOT help here: react-router 6.30 hands updaters the
 *    render-time params snapshot, not the live URL.)
 */
export function useUrlParamState<T>(
  key: string,
  decode: (raw: string | null) => T,
  encode: (v: T) => string | null,
  opts?: {
    /** Gate URL write-back (default true). Keep false until async validation data is ready. */
    syncWhen?: boolean;
  },
): [T, (v: T | ((prev: T) => T)) => void] {
  const [params, setParams] = useSearchParams();
  const [value, setValue] = useState<T>(() => decode(params.get(key)));
  const syncWhen = opts?.syncWhen ?? true;

  useEffect(() => {
    if (!syncWhen) return;
    const next = encode(value);
    // Read + build from the LIVE URL (synchronously current after each
    // replace navigation), and skip no-op writes — see the header note
    // on why the functional setParams form can't be trusted here.
    const qs = new URLSearchParams(window.location.search);
    const curr = qs.get(key);
    if (next === curr) return;
    if (next === null) qs.delete(key);
    else qs.set(key, next);
    setParams(qs, { replace: true });
    // encode is intentionally excluded: inline encoders change identity
    // every render (same convention as useUrlTimeRange's sync effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, syncWhen, key, setParams]);

  const set = useCallback((v: T | ((prev: T) => T)) => setValue(v), []);
  return [value, set];
}
